import React from 'react';
import { IconAlert, IconCheckCircle, IconInfo } from '../icons';

/**
 * Public-site primitives.
 *
 * Same tokens and shapes as the admin panel, tuned for marketing and form
 * pages: roomier spacing, larger type, and CTAs that carry brand gold.
 */

/* ── Layout ────────────────────────────────────────────────────────────────── */

/**
 * The public site's outer bound, shared by the page content and the chrome
 * around it (header, mobile nav, footer) so nothing is inset against anything
 * else. 1440px: wide enough that a three-up grid on a large monitor is three
 * usable columns rather than three slivers inside a sea of margin, and still
 * a comfortable measure for the prose sections, which set their own narrower
 * `max-w` on top of it.
 */
export const SITE_WIDTH = 'max-w-[90rem]';

const widths = {
  sm: 'max-w-xl',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-4xl',
  full: SITE_WIDTH
};

export const Container = ({ width = 'xl', className = '', children }) => (
  <div className={`mx-auto w-full ${widths[width]} px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const Section = ({ className = '', children, ...rest }) => (
  <section className={`py-10 sm:py-12 ${className}`} {...rest}>
    {children}
  </section>
);

export const SectionHeading = ({ eyebrow, title, description, align = 'left', className = '' }) => (
  <div className={`${align === 'center' ? 'text-center' : ''} ${className}`}>
    {eyebrow && (
      <p className="mb-2 text-[11px] font-700 uppercase tracking-[0.14em] text-brand-600">{eyebrow}</p>
    )}
    <h2 className="font-display text-2xl font-700 tracking-tight text-ink-900 sm:text-3xl">{title}</h2>
    {description && (
      <p className={`mt-3 text-[15px] leading-relaxed text-ink-600 ${align === 'center' ? 'mx-auto max-w-2xl' : 'max-w-2xl'}`}>
        {description}
      </p>
    )}
  </div>
);

/* ── Surfaces ──────────────────────────────────────────────────────────────── */

export const Card = ({ className = '', children, ...rest }) => (
  <div className={`rounded-xl border border-ink-200 bg-white shadow-card ${className}`} {...rest}>
    {children}
  </div>
);

export const Panel = ({ className = '', children }) => (
  <div className={`rounded-2xl border border-ink-200 bg-white p-6 shadow-card sm:p-8 ${className}`}>
    {children}
  </div>
);

/* ── Buttons ───────────────────────────────────────────────────────────────── */

const buttonVariants = {
  primary: 'bg-brand-800 text-white hover:bg-brand-700 focus-visible:ring-brand-600',
  /** The customer-facing "do the thing" CTA — the orange in the header and hero. */
  accent: 'bg-accent-500 text-white hover:bg-accent-600 focus-visible:ring-accent-600',
  gold: 'bg-gold-500 text-brand-900 hover:bg-gold-400 focus-visible:ring-gold-600',
  /** Courier-facing CTA. Couriers are blue everywhere, merchants gold. */
  info: 'bg-info-500 text-white hover:bg-info-600 focus-visible:ring-info-600',
  secondary:
    'border border-ink-300 bg-white text-ink-700 hover:bg-ink-50 hover:text-ink-900 focus-visible:ring-brand-600',
  /** Secondary on a dark ground — the footer banner and hero. */
  onDark: 'border border-white/30 bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white',
  ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900 focus-visible:ring-brand-600',
  danger: 'bg-danger-600 text-white hover:bg-danger-700 focus-visible:ring-danger-600'
};

const buttonSizes = {
  sm: 'h-9 gap-1.5 px-3.5 text-[13px]',
  md: 'h-11 gap-2 px-5 text-sm',
  lg: 'h-12 gap-2 px-6 text-[15px]'
};

export const Button = ({
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  className = '',
  loading = false,
  disabled = false,
  children,
  ...rest
}) => {
  const isNativeButton = Component === 'button';
  return (
    <Component
      {...(isNativeButton ? { type: rest.type || 'button', disabled: disabled || loading } : {})}
      className={`inline-flex select-none items-center justify-center rounded-lg font-600 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${buttonVariants[variant]} ${buttonSizes[size]} ${className}`}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </Component>
  );
};

export const Spinner = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

/* ── Feedback ──────────────────────────────────────────────────────────────── */

const alertTones = {
  success: { wrap: 'bg-success-50 ring-success-100 text-success-700', icon: IconCheckCircle },
  warning: { wrap: 'bg-warning-50 ring-warning-100 text-warning-700', icon: IconAlert },
  danger: { wrap: 'bg-danger-50 ring-danger-100 text-danger-700', icon: IconAlert },
  info: { wrap: 'bg-info-50 ring-info-100 text-info-700', icon: IconInfo }
};

export const Alert = ({ tone = 'info', title, children, className = '', ...rest }) => {
  const { wrap, icon: Icon } = alertTones[tone];
  return (
    <div
      className={`flex items-start gap-3 rounded-xl px-4 py-3.5 ring-1 ring-inset ${wrap} ${className}`}
      {...rest}
    >
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-600">{title}</p>}
        {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
      </div>
    </div>
  );
};

const badgeTones = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-100',
  gold: 'bg-gold-50 text-gold-700 ring-gold-100',
  neutral: 'bg-ink-100 text-ink-600 ring-ink-200',
  success: 'bg-success-50 text-success-700 ring-success-100'
};

export const Badge = ({ tone = 'brand', className = '', children }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-600 ring-1 ring-inset ${badgeTones[tone]} ${className}`}
  >
    {children}
  </span>
);

/* ── Form controls (share .pb-control with the admin panel) ────────────────── */

/**
 * Labelled form field. Mirrors the admin Field: `required` shows the indicator
 * and sets aria-required, `error` flags the control aria-invalid and links the
 * message to it with aria-describedby.
 */
export const Field = ({ label, hint, required, htmlFor, error, className = '', children }) => {
  const errorId = error && htmlFor ? `${htmlFor}-error` : undefined;
  const hintId = !error && hint && htmlFor ? `${htmlFor}-hint` : undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        'aria-invalid': error ? true : children.props['aria-invalid'],
        'aria-describedby': errorId || hintId || children.props['aria-describedby'],
        'aria-required': required || children.props['aria-required'] || undefined
      })
    : children;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-600 text-ink-800">
          {label}
          {required && (
            <>
              <span className="ml-0.5 text-danger-600" aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </>
          )}
        </label>
      )}
      {control}
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-xs font-600 text-danger-700">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-xs text-ink-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export const Input = ({ className = '', ...rest }) => (
  <input className={`pb-control ${className}`} {...rest} />
);

export const Select = ({ className = '', children, ...rest }) => (
  <select className={`pb-control pb-select ${className}`} {...rest}>
    {children}
  </select>
);

export const Textarea = ({ className = '', ...rest }) => (
  <textarea className={`pb-control min-h-[112px] py-2.5 ${className}`} {...rest} />
);

/* ── Content bits ──────────────────────────────────────────────────────────── */

/** Bulleted benefit line with a brand tick — used across the marketing pages. */
export const TickItem = ({ children, className = '' }) => (
  <li className={`flex items-start gap-3 ${className}`}>
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
    <span className="text-[15px] leading-relaxed text-ink-700">{children}</span>
  </li>
);

export const FeatureCard = ({ icon: Icon, title, description, tone = 'brand' }) => (
  <div className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-card">
    {Icon && (
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ${
          tone === 'gold'
            ? 'bg-gold-50 text-gold-700 ring-gold-100'
            : 'bg-brand-50 text-brand-700 ring-brand-100'
        }`}
      >
        <Icon size={19} />
      </span>
    )}
    <div className="min-w-0">
      <h3 className="font-display text-[15px] font-700 text-ink-900">{title}</h3>
      {description && <p className="mt-1 text-sm leading-relaxed text-ink-600">{description}</p>}
    </div>
  </div>
);
